import React, { Suspense } from 'react'
import { Row } from 'components/common/layout'
import {
  ApplicationFulfillment,
  DashboardScoutRequestDetail,
  DashboardScoutRequestOneQueryParams,
} from 'types/common'
import { BaseText } from 'components/common/text'
import { theme } from 'infra/theme'
import { LineLimitedText } from 'components/scouts/profile-detail/desc-more-view'
import { CareerDetail } from 'components/scouts/profile/career-detail'
import { UserBaseInfo } from 'components/scouts/profile/common/user-base-info'
import { Tag, TagContainer } from 'components/common/tag'
import { Attachments } from 'components/scouts/profile-detail/attachments'
import { DevEducation } from 'components/scouts/profile-detail/dev-education'
import { OtherActivityDetail } from 'components/scouts/profile/other-activity-detail'
import Image from 'next/legacy/image'
import { convertNumberToHyphenPhone } from 'utils/displays'
import { useRouter } from 'next/router'
import { analytics } from 'infra/analytics'
import { MyLink } from 'components/common/link'
import { LanguageSkillDetail } from 'components/scouts/profile/language-skill-detail'
import {
  ProfileDetailContainer,
  ProfileDetailSection,
  ProfileDetailContactText,
  ProfileDetailContentLayout,
  ProfileDetailInformText,
  ProfileDetailPlaceholderCard,
  ProfileDetailPlaceholderText,
  ProfileDetailSectionTitle,
  ProfileDetailTextButton,
  ProfileDetailTextButtonWrapper,
  ProfileDetailTextWrapper,
  ProfileDetailUnpublishedBanner,
  ProfileDetailUserInfoCardWrapper,
} from 'components/scouts/profile-detail/styles'
import { postRequestDashboardScoutProposalRequestResume } from 'apis/request'
import { InformationBox } from 'components/misc/information-box'
import { useStartupUser } from 'apis/hooks'
import { ProfileDetailConditionSection } from 'components/scouts/profile-detail/condition-section'
import { useIsDesktop, useIsLoggedIn } from 'utils/hooks'
import { ProfileDetailAdditionalInfo } from 'components/scouts/profile-detail/additional-info'
import { useStores } from 'stores/globals'
import { observer } from 'mobx-react'
import {
  BlurLayer,
  LoginOverlay,
} from 'components/scouts/profile/not-open-contents'

const SectionPlaceholder: React.FC<{ placeholder: string; button: string }> = ({
  placeholder,
  button,
}) => {
  return (
    <MyLink
      href='/scouts/my-profile'
      onClick={() =>
        analytics.track('click_sp_go_to_fill_profile_from_preview')
      }
    >
      <ProfileDetailPlaceholderCard>
        <ProfileDetailPlaceholderText>
          ❗️ {placeholder}
        </ProfileDetailPlaceholderText>
        <ProfileDetailTextButton>{button}</ProfileDetailTextButton>
      </ProfileDetailPlaceholderCard>
    </MyLink>
  )
}

const ProfileFillButton: React.FCC = ({ children }) => {
  return (
    <ProfileDetailTextButtonWrapper>
      <MyLink
        href='/scouts/my-profile'
        onClick={() =>
          analytics.track('click_sp_go_to_fill_profile_from_preview')
        }
      >
        <ProfileDetailTextButton>{children}</ProfileDetailTextButton>
      </MyLink>
    </ProfileDetailTextButtonWrapper>
  )
}

export interface ProfileDetailApplicationData {
  coverLetter?: string | null
  joinReason?: string | null
  fitReason?: string | null
  fulfillments?: ApplicationFulfillment
  email?: string
  phone?: string
}

export const ProfileDetail: React.FC<{
  data: DashboardScoutRequestDetail & ProfileDetailApplicationData
  params?: DashboardScoutRequestOneQueryParams
  curationId?: number
  exampleId?: number // 합격하는 이력서 예시 보여줄 때
  hasCondition?: boolean // 지원자일 때 등 구직조건 섹션이 필요 없으면 false
  isChatBlocked?: boolean
  isNarrow?: boolean
  shouldInduceToFill?: boolean // 인재 사이드에서 미리보기할 때 미완된 정보를 표시하고 작성 유도하기 위함
}> = observer(
  ({
    data,
    params,
    curationId,
    hasCondition = true,
    isNarrow,
    isChatBlocked,
    shouldInduceToFill,
    exampleId,
  }) => {
    const router = useRouter()
    const { data: startupUser } = useStartupUser()
    const { loginStore } = useStores()
    const isLoggedIn = useIsLoggedIn()
    const proposalId = Number(router.query.proposalId) || null
    const isChatRooms = router.pathname.includes('chat-rooms')
    const isScoutChatRoom = isChatRooms && !!proposalId
    const hasAttachment = data.files.length > 0 || data.links.length > 0
    const isExampleNotOpen = !!exampleId && !isLoggedIn
    const isDesktop = useIsDesktop()
    return (
      <ProfileDetailContainer isNarrow={isNarrow}>
        {/* 비공개되었거나 삭제되었을 경우, 어드민만 프로필이 보임 */}
        {!!startupUser?.isAdmin && !data.isPublished && !data.isDeleted && (
          <ProfileDetailUnpublishedBanner>
            <span>주의 ! 비공개된 프로필입니다</span>
            <span>주의 ! 비공개된 프로필입니다</span>
            <span>주의 ! 비공개된 프로필입니다</span>
          </ProfileDetailUnpublishedBanner>
        )}
        <ProfileDetailUserInfoCardWrapper>
          <UserBaseInfo
            data={{ ...data, requestId: data.id }}
            exampleId={exampleId}
            params={params}
            isNarrow={isNarrow}
          />
          {data.email && data.phone && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>연락처</ProfileDetailSectionTitle>
              <Row style={{ gap: 8 }}>
                <Image
                  src='/phone-call.svg'
                  width={18}
                  height={18}
                  alt='phone'
                />
                <ProfileDetailContactText href={`tel:${data.phone}`}>
                  {convertNumberToHyphenPhone(data.phone)}
                </ProfileDetailContactText>
              </Row>
              <Row style={{ gap: 8 }}>
                <Image src='/mail.svg' width={18} height={18} alt='mail' />
                <ProfileDetailContactText href={`mailto:${data.email}`}>
                  {data.email}
                </ProfileDetailContactText>
              </Row>
            </ProfileDetailSection>
          )}
          <ProfileDetailAdditionalInfo data={data} isNarrow={isNarrow} />
        </ProfileDetailUserInfoCardWrapper>

        {/* 자기소개 */}
        {shouldInduceToFill &&
          !data.introduction &&
          !data.personalities.length && (
            <SectionPlaceholder
              placeholder='"자기소개" 가 없어요'
              button='작성하러 가기 (+ 최대 3점)'
            />
          )}
        {(data.introduction || data.personalities.length > 0) && (
          <ProfileDetailSection>
            <ProfileDetailSectionTitle>자기소개</ProfileDetailSectionTitle>
            {data.personalities.length > 0 && (
              <TagContainer>
                {data.personalities.map((keyword) => (
                  <Tag
                    key={keyword.id}
                    dangerouslySetInnerHTML={{ __html: keyword.name }}
                  />
                ))}
              </TagContainer>
            )}
            <ProfileDetailTextWrapper>
              <LineLimitedText
                text={data.introduction || ''}
                lineClamp={exampleId ? (isDesktop ? 10 : 15) : 3}
                hasLineBreak={!!exampleId}
                onClick={
                  isExampleNotOpen
                    ? () => {
                        analytics.track(
                          'click_kakao_login_modal_open_by_intro_more_btn',
                        )
                        loginStore.setIsModalOpen(true)
                      }
                    : undefined
                }
              />
            </ProfileDetailTextWrapper>
            {shouldInduceToFill &&
              (!data.introduction || data.introduction.length < 20) && (
                <ProfileFillButton>
                  {`"자기소개" 보충하러 가기 (+ 2점)`}
                </ProfileFillButton>
              )}
          </ProfileDetailSection>
        )}

        {/* LoginOverlay가 덮일 영역 */}
        <ProfileDetailContainer
          isNarrow={isNarrow}
          style={isExampleNotOpen ? { userSelect: 'none' } : undefined}
        >
          {/* 스타트업에 기대하는 점 */}
          {shouldInduceToFill &&
            !data.expectation.description &&
            !data.expectation.keywords.length && (
              <SectionPlaceholder
                placeholder='"스타트업에 기대하는 점" 이 없어요'
                button='작성하러 가기 (+ 최대 3점)'
              />
            )}
          {(data.expectation.description ||
            data.expectation.keywords.length > 0) && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>
                스타트업에 기대하는 점
              </ProfileDetailSectionTitle>
              {data.expectation.keywords.length > 0 && (
                <TagContainer>
                  {data.expectation.keywords.map((keyword) => (
                    <Tag
                      key={keyword.id}
                      dangerouslySetInnerHTML={{ __html: keyword.name }}
                    />
                  ))}
                </TagContainer>
              )}
              {data.expectation.description && (
                <ProfileDetailTextWrapper>
                  <LineLimitedText
                    text={data.expectation.description}
                    onClick={isExampleNotOpen ? () => {} : undefined}
                  />
                </ProfileDetailTextWrapper>
              )}
              {shouldInduceToFill &&
                data.expectation.description.length < 20 && (
                  <ProfileFillButton>
                    {`"스타트업에 기대하는 점" 보충하러 가기 (+ 2점)`}
                  </ProfileFillButton>
                )}
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 이직하는 이유 */}
          {shouldInduceToFill &&
            !data.jobSearchingReason &&
            data.workLevel?.id === 3 && (
              <SectionPlaceholder
                placeholder='"이직하는 이유" 가 없어요'
                button='작성하러 가기 (+ 3점)'
              />
            )}
          {data.jobSearchingReason && data.workLevel?.id === 3 && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>
                이직하는 이유
              </ProfileDetailSectionTitle>
              <ProfileDetailTextWrapper>
                <LineLimitedText
                  text={data.jobSearchingReason}
                  onClick={isExampleNotOpen ? () => {} : undefined}
                />
              </ProfileDetailTextWrapper>
              {shouldInduceToFill && data.jobSearchingReason.length < 20 && (
                <ProfileFillButton>
                  {`"이직하는 이유" 보충하러 가기 (+ 3점)`}
                </ProfileFillButton>
              )}
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 경력 */}
          {!!data.careers?.length && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>경력</ProfileDetailSectionTitle>
              <ProfileDetailContentLayout>
                {data.careers.map((career, index) => (
                  <div key={index}>
                    <CareerDetail
                      career={career}
                      isNarrow={isNarrow}
                      isNotOpen={isExampleNotOpen}
                    />
                    {shouldInduceToFill && !career.description && (
                      <ProfileFillButton>{`"경력 > 상세 설명" 작성하러 가기 (+ 2점)`}</ProfileFillButton>
                    )}
                  </div>
                ))}
              </ProfileDetailContentLayout>
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 직무 관련 활동 */}
          {!!data.otherActivities?.length && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>
                직무 관련 활동
              </ProfileDetailSectionTitle>
              <ProfileDetailContentLayout>
                {data.otherActivities.map((item, index) => (
                  <OtherActivityDetail
                    key={index}
                    data={item}
                    isNarrow={isNarrow}
                    isNotOpen={isExampleNotOpen}
                  />
                ))}
              </ProfileDetailContentLayout>
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 학력 */}
          {data.educations.length > 0 && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>학력</ProfileDetailSectionTitle>
              <ProfileDetailContentLayout>
                {data.educations.map((item) => (
                  <DevEducation
                    key={item.id}
                    education={item}
                    isNarrow={isNarrow}
                  />
                ))}
              </ProfileDetailContentLayout>
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 외국어 능력 */}
          {shouldInduceToFill && !data.languageSkills.length && (
            <SectionPlaceholder
              placeholder='"외국어 능력" 이 없어요'
              button='작성하러 가기 (+ 1점 / 1개)'
            />
          )}
          {data.languageSkills.length > 0 && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>외국어 능력</ProfileDetailSectionTitle>
              <ProfileDetailContentLayout>
                {data.languageSkills.map((item) => (
                  <div key={item.id}>
                    <LanguageSkillDetail data={item} isNarrow={isNarrow} />
                    {shouldInduceToFill && !item.description && (
                      <ProfileFillButton>{`"외국어 능력 > 상세 설명" 작성하러 가기`}</ProfileFillButton>
                    )}
                  </div>
                ))}
              </ProfileDetailContentLayout>
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 첨부 자료 */}
          {shouldInduceToFill && !hasAttachment && (
            <SectionPlaceholder
              placeholder='"첨부 자료" 가 없어요'
              button='업로드하러 가기 (+ 3점)'
            />
          )}
          {(isChatRooms || hasAttachment) && (
            <ProfileDetailSection>
              <Row style={{ gap: 16 }}>
                <ProfileDetailSectionTitle
                  className={
                    params?.hasAccessibleAttachment ? 'highlighted' : ''
                  }
                >
                  첨부 자료
                </ProfileDetailSectionTitle>
                {!isChatBlocked &&
                  isScoutChatRoom &&
                  (!hasAttachment || !data?.isResumeAllowed) && (
                    <BaseText
                      style={{
                        color: theme.color.secondary,
                        cursor: 'pointer',
                      }}
                      onClick={async () => {
                        if (!proposalId) return
                        analytics.track('click_dcr_resume_request_btn', {
                          requestId: data.id,
                          proposalId,
                        })
                        await postRequestDashboardScoutProposalRequestResume(
                          proposalId,
                        )
                      }}
                    >
                      {!hasAttachment ? '업로드 요청하기' : '공개 요청하기'}
                    </BaseText>
                  )}
              </Row>
              <Attachments
                files={data.files}
                links={data.links}
                requestId={data.id}
                curationId={curationId}
                curatedAt={data.curatedAt}
              />
              <div style={{ paddingLeft: 40 }}>
                {hasAttachment && !data.isResumeAllowed ? (
                  <ProfileDetailInformText>
                    {
                      '인재의 개인정보를 가린 파일만 볼 수 있어요.\n메시지를 보내면 인재가 원본 파일과 링크를 공개할 수 있어요.'
                    }
                  </ProfileDetailInformText>
                ) : data.links.length > 0 ? (
                  <InformationBox isNarrow>
                    링크에는 인재의 개인 정보가 포함되어 있을 수 있으며, 이를
                    통해 직접 채용할 경우 위약금이 부과될 수 있습니다.
                  </InformationBox>
                ) : (
                  ''
                )}
              </div>
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {/* 관심 스킬 */}
          {data.techStacks.length > 0 && (
            <ProfileDetailSection>
              <ProfileDetailSectionTitle>관심 스킬</ProfileDetailSectionTitle>
              <TagContainer>
                {data.techStacks.map((item) => (
                  <Tag
                    key={item.id}
                    dangerouslySetInnerHTML={{ __html: item.name }}
                  />
                ))}
              </TagContainer>
              {isExampleNotOpen && <BlurLayer top={24} />}
            </ProfileDetailSection>
          )}

          {isExampleNotOpen && (
            <LoginOverlay>
              {'그룹바이 로그인하고\n모든 이력서를 무료로 확인해보세요'}
            </LoginOverlay>
          )}
        </ProfileDetailContainer>

        {/* 구직 조건 */}
        {hasCondition && (
          <Suspense>
            <ProfileDetailConditionSection data={data} params={params} />
          </Suspense>
        )}
      </ProfileDetailContainer>
    )
  },
)
